import type { BillingEntityInput, TicketSettings } from '@pulsify/shared/dto'
import { type FormEvent, useEffect, useId, useState } from 'react'
import { toast } from 'sonner'
import { useSaveInvoiceIssuer } from '~/entities/ticket/model/use-tickets'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { Button } from '~/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/shared/ui/dialog'
import { Input } from '~/shared/ui/input'
import { Label } from '~/shared/ui/label'
import { Switch } from '~/shared/ui/switch'

function draftFrom(settings: TicketSettings): BillingEntityInput {
  const issuer = settings.invoice.issuer
  return {
    legalName: issuer?.legalName ?? '',
    street: issuer?.street ?? null,
    postalCode: issuer?.postalCode ?? null,
    city: issuer?.city ?? null,
    country: issuer?.country ?? 'AT',
    vatId: issuer?.vatId ?? null,
    registrationNumber: issuer?.registrationNumber ?? null,
    email: issuer?.email ?? null,
  }
}

/** Edits the company on ticket invoices, and lets a profile sell as a company of its own. */
export function InvoiceIssuerDialog({
  open,
  onOpenChange,
  businessId,
  hostProfileId,
  settings,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  businessId: string
  hostProfileId?: string
  settings: TicketSettings
}) {
  const save = useSaveInvoiceIssuer(businessId, hostProfileId)
  const id = useId()
  const [draft, setDraft] = useState(() => draftFrom(settings))
  const [own, setOwn] = useState(settings.invoice.ownIssuer)

  useEffect(() => {
    if (!open) return
    setDraft(draftFrom(settings))
    setOwn(settings.invoice.ownIssuer)
  }, [open, settings])

  const field = (key: keyof BillingEntityInput) => ({
    id: `${id}-${key}`,
    value: (draft[key] as string | null) ?? '',
    onChange: (event: { target: { value: string } }) =>
      setDraft((current) => ({ ...current, [key]: event.target.value })),
  })

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    event.stopPropagation()
    save.mutate(
      { details: draft, own },
      {
        onSuccess: () => {
          toast.success(m.ticketsInvoiceSaved())
          onOpenChange(false)
        },
        onError: (error) => toast.error(getErrorMessageFromError(error)),
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{m.ticketsInvoiceSeller()}</DialogTitle>
          <DialogDescription>{m.ticketsIssuerDescription()}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {hostProfileId ? (
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor={`${id}-own`}>{m.ticketsIssuerOwn()}</Label>
              <Switch id={`${id}-own`} checked={own} onCheckedChange={setOwn} />
            </div>
          ) : null}
          <div className="space-y-1.5">
            <Label htmlFor={`${id}-legalName`}>{m.profileBillingLegalName()}</Label>
            <Input {...field('legalName')} required />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${id}-street`}>{m.profileBillingStreet()}</Label>
            <Input {...field('street')} required />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor={`${id}-postalCode`}>{m.profileBillingPostalCode()}</Label>
              <Input {...field('postalCode')} required />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label htmlFor={`${id}-city`}>{m.profileBillingCity()}</Label>
              <Input {...field('city')} required />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${id}-vatId`}>{m.profileBillingVatId()}</Label>
            <Input {...field('vatId')} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {m.cancel()}
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {m.profileBillingSave()}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
